// Placeholder extraction, validation, and renaming.
//
// Extraction is the single source of truth for a template's field list: the
// server derives `placeholderNames` from the document on write, and the browser
// builds the fill form from the same walk. A client cannot declare fields the
// document does not contain (FR-012).

import {
  PLACEHOLDER_NAME_MAX_LENGTH,
  PLACEHOLDER_NAME_PATTERN,
  type BlockNode,
  type InlineNode,
  type ListItem,
  type Paragraph,
  type TemplateDocument,
} from './schema.js';

export function isValidPlaceholderName(name: unknown): name is string {
  return (
    typeof name === 'string' &&
    name.length > 0 &&
    name.length <= PLACEHOLDER_NAME_MAX_LENGTH &&
    PLACEHOLDER_NAME_PATTERN.test(name)
  );
}

/**
 * The comparison form used to decide whether two names are *confusably* the same
 * (FR-030). Comparison-only: it is never stored, never rendered, and never
 * written back into a document. Interior whitespace is preserved on purpose —
 * "Client  Name" is a different name from "Client Name", not a confusable of it.
 */
export function normalisePlaceholderName(name: string): string {
  return name.trim().toLocaleLowerCase();
}

/**
 * The existing name `candidate` would be confused with: same normalised form,
 * different exact form. Returns `undefined` when there is none.
 *
 * This is the inverse of the exact-match dedup in {@link extractPlaceholderNames}
 * — that decides when two names *are* one field, this reports when they only look
 * it. An exact match is therefore never confusable: if `candidate` is already in
 * `existingNames` verbatim, it merges into that field and there is nothing to
 * warn about, even if a differently-cased twin also exists.
 */
export function findConfusableName(
  candidate: string,
  existingNames: readonly string[],
): string | undefined {
  if (existingNames.includes(candidate)) return undefined;
  const target = normalisePlaceholderName(candidate);
  return existingNames.find((existing) => normalisePlaceholderName(existing) === target);
}

function forEachInlineNode(doc: TemplateDocument, visit: (node: InlineNode) => void): void {
  const visitParagraph = (p: Paragraph): void => {
    for (const node of p.content ?? []) visit(node);
  };
  const visitListItem = (item: ListItem): void => {
    for (const p of item.content) visitParagraph(p);
  };
  const visitBlock = (block: BlockNode): void => {
    switch (block.type) {
      case 'paragraph':
        visitParagraph(block);
        return;
      case 'heading':
        for (const node of block.content ?? []) visit(node);
        return;
      case 'bulletList':
      case 'orderedList':
        for (const item of block.content) visitListItem(item);
        return;
    }
  };
  for (const block of doc.content) visitBlock(block);
}

/**
 * Distinct placeholder names, in document order.
 *
 * Deduplication is what makes FR-009 true: three occurrences of one name are one
 * form field, one submitted value, and three substitutions.
 */
export function extractPlaceholderNames(doc: TemplateDocument): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  forEachInlineNode(doc, (node) => {
    if (node.type !== 'placeholder') return;
    const { name } = node.attrs;
    if (seen.has(name)) return;
    seen.add(name);
    names.push(name);
  });
  return names;
}

/**
 * Rename every occurrence of `oldName` to `newName` (FR-006a).
 *
 * Renaming onto an existing name is allowed and merges the two fields — the
 * editor must be able to express that, and extraction dedupes the result.
 *
 * Returns a new document; the input is never mutated.
 */
export function renamePlaceholder(
  doc: TemplateDocument,
  oldName: string,
  newName: string,
): TemplateDocument {
  if (!isValidPlaceholderName(newName)) {
    throw new Error(`invalid placeholder name: ${JSON.stringify(newName)}`);
  }

  const rewriteInline = (node: InlineNode): InlineNode =>
    node.type === 'placeholder' && node.attrs.name === oldName
      ? { type: 'placeholder', attrs: { name: newName } }
      : node;

  const rewriteParagraph = (p: Paragraph): Paragraph =>
    p.content ? { ...p, content: p.content.map(rewriteInline) } : p;

  const rewriteBlock = (block: BlockNode): BlockNode => {
    switch (block.type) {
      case 'paragraph':
        return rewriteParagraph(block);
      case 'heading':
        return block.content ? { ...block, content: block.content.map(rewriteInline) } : block;
      case 'bulletList':
      case 'orderedList':
        return {
          ...block,
          content: block.content.map((item) => ({
            ...item,
            content: item.content.map(rewriteParagraph),
          })),
        };
    }
  };

  return { ...doc, content: doc.content.map(rewriteBlock) };
}
